// Tenant › Email: a one-off email to one of the league's admins, written in the
// same compose panel as a signup's profile (OutreachCompose: templates, signer,
// merge fields, the server's preview, and Send only through its confirm), and
// every one-off email about this league with its replies and bounces.
//
// GET /platform/leagues/:id/outreach lists who it can go to (the signup's owner,
// else the first admin, first) and the emails; the preview and the send are
// POST /platform/leagues/:id/outreach/{preview,send} with `admin_user_id`.

import { useState } from 'react'
import { api } from '@/api'
import { Badge, Card, CardHeader, ErrorState, FormField, RelativeTime, SelectInput, SkeletonRows } from '@/components/ui'
import { titleCase } from '@/lib/format'
import { invalidateQueries, useQuery } from '@/lib/query'
import { EMAIL_STATUS_LABELS, EMAIL_STATUS_TONE } from '@/lib/signups'
import { OutreachCompose, type OutreachTarget } from '@/pages/signups/OutreachCompose'
import { useTenant } from '../tenantContext'

export default function EmailTab() {
  const { tenant } = useTenant()
  const data = useQuery(['tenants', tenant.id, 'outreach'], (signal) => api.growth.outreach.tenant(tenant.id, signal))
  const options = useQuery(['signups', 'outreach-options'], (signal) => api.growth.outreach.options(signal))
  const [picked, setPicked] = useState<number | null>(null)

  const recipients = data.data?.recipients ?? []
  const chosen = recipients.find((r) => r.admin_user_id === picked) ?? recipients.find((r) => r.default) ?? null
  const adminId = chosen?.admin_user_id ?? null
  const target: OutreachTarget = {
    to: chosen?.email ?? null,
    noAddress: "This league has no admin with an email address. Add one on the People tab first.",
    previewKey: ['tenants', tenant.id, 'outreach-preview', adminId],
    preview: (draft, signal) => api.growth.outreach.previewTenant(tenant.id, { ...draft, admin_user_id: adminId }, signal),
    send: (draft) => api.growth.outreach.sendTenant(tenant.id, { ...draft, admin_user_id: adminId }),
    onSent: () => {
      invalidateQueries('tenants')
      invalidateQueries('signups')
    },
  }

  const emails = data.data?.emails ?? []
  return (
    <div className="grid gap-5">
      {data.status === 'error' ? (
        <Card>
          <ErrorState error={data.error} title="Couldn't load this league's emails" onRetry={data.refetch} />
        </Card>
      ) : !data.data || !options.data ? (
        options.status === 'error' ? (
          <Card>
            <ErrorState error={options.error} title="Couldn't load the compose panel" onRetry={options.refetch} />
          </Card>
        ) : (
          <Card>
            <SkeletonRows rows={4} label="Loading the compose panel" />
          </Card>
        )
      ) : (
        <OutreachCompose
          target={target}
          options={options.data}
          recipient={
            recipients.length ? (
              <FormField label="To">
                {({ id }) => (
                  <SelectInput
                    id={id}
                    value={adminId === null ? '' : String(adminId)}
                    options={recipients.map((r) => ({ value: String(r.admin_user_id), label: `${r.name ? `${r.name} · ` : ''}${r.email} (${titleCase(r.role)})` }))}
                    onChange={(e) => setPicked(Number(e.target.value))}
                  />
                )}
              </FormField>
            ) : null
          }
        />
      )}

      <Card flush className="overflow-hidden">
        <div className="px-4 pt-4">
          <CardHeader
            title="Emails to this league"
            description="Every one-off email about this league, newest first. Replies and bounces come from the inbox check."
          />
        </div>
        {data.status === 'loading' ? (
          <SkeletonRows rows={3} label="Loading emails" />
        ) : emails.length === 0 ? (
          <p className="px-4 pb-4 text-[13px] text-muted">No emails yet.</p>
        ) : (
          <ul aria-label="Emails to this league" className="divide-y divide-line border-t border-line">
            {emails.map((e) => (
              <li key={e.id} className="grid gap-1 px-4 py-3 text-[13px]">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 break-words font-semibold text-ink">{e.subject}</span>
                  <Badge tone={EMAIL_STATUS_TONE[e.status]}>{EMAIL_STATUS_LABELS[e.status]}</Badge>
                  {e.replied_at ? <Badge tone="good">Replied</Badge> : null}
                  {e.bounced_at ? <Badge tone="bad">{e.bounce_kind === 'soft' ? 'Soft bounce' : 'Bounced'}</Badge> : null}
                </div>
                <p className="break-words text-xs text-muted">
                  To <span className="font-mono [overflow-wrap:anywhere]">{e.email}</span> · signed by {e.signer_name} · <RelativeTime value={e.sent_at ?? e.created_at} />
                  {e.status === 'dry_run' ? ' · sending was off, nothing left' : e.error ? ` · ${e.error}` : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
